// THE CRUST — Mineral and Regolith, which between them are the ground of every rocky world.
//
// Sixty-four kubes wear these two canvases: thirty-two "<X> Mineral" and thirty-two "<X> Regolith", and every pair
// shares its colour, its composition and its density exactly (Arid Mineral and Arid Regolith are both #D2B48C, both
// 3.35), so the pattern is the only thing that tells a pair apart.
//
// THE OWNER'S PICKS (Oct 12, the "Crust Patterns" page): the REGOLITH is SAND GRAINS — thousands of round grains of
// mixed size, a few darker — and the MINERAL is ROCK PLATES — irregular plates of rock with dark joints between them.
// They replace the right-triangle stipple both wore since Sep 29 (in git, 0e04cfc and before, if it is ever wanted
// back). The owner's note with the picks: "only use close, not far" — drawn as they look up close, with no tuning for
// how they read from orbit (the stipple's reason for being, Sep 29, no longer holds).
//
// Both slots are SHARED BY DESIGN (thirty-two kubes each), so both obey the shared-slot rules that
// createFloatGlassTexture and createGasVesselTexture spell out: drawn in black over white, because the canvas is
// multiplied by each kube's own hex, and from a fixed seed rather than from the hex of whichever kube is drawn first,
// so the ground is the same canvas for every player in every session. Neither function takes a colour. Each is
// brought to the mean brightness the stipple had — about 0.90 (Mineral) and 0.93 (Regolith) — which every world's
// ground colour was tuned against. Both tile: every grain is drawn again across the edges it crosses, and the plates'
// distances wrap, so neighbouring kubes join up.
//
// THREE-free, and it returns a canvas rather than a texture, so doc/world-generation.html — which runs its own,
// older three.js — can draw its preview with this code instead of keeping a copy. TextureEngine wraps it for the game.

const SIZE = 1024;

// A fixed, seeded generator (an LCG): the same canvas in every engine, every session.
function rng(seed) { let x = seed >>> 0; return () => ((x = (Math.imul(x, 1664525) + 1013904223) >>> 0) / 4294967296); }
function blank() {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, SIZE, SIZE);
    return { canvas, ctx };
}
// THE MEANS ARE SET BY CONSTANTS, not measured at draw time: a texture maker does no per-pixel pass
// (tests/material-looks.test.mjs holds every new maker to that). Each K scales the pattern's darkness so the canvas
// averages the stipple's brightness, 0.93 (Regolith) and 0.90 (Mineral), measured on these exact seeds in Chrome
// (scratchpad crustmean.mjs); tests/material-looks.test.mjs pins the constants with the seeds.
export const REGOLITH_K = 0.3874, MINERAL_K = 0.3984;

// REGOLITH: SAND GRAINS. 2,600 round grains, 3 to 12 texels across, each a random share of black (0.15–0.6, scaled
// by REGOLITH_K), drawn again across any edge it crosses so the canvas tiles.
export function drawRegolithCrust() {
    const scale = arguments[0], k = Number.isFinite(scale) ? scale : REGOLITH_K;   // only the measuring harness passes a scale; the game passes nothing (no colour, no size)
    const { canvas, ctx } = blank(), R = rng(0x5a17d);
    for (let i = 0; i < 2600; i++) {
        const r = 3.2 + R() * 8.8, x = R() * SIZE, y = R() * SIZE, alpha = Math.min(1, (0.15 + R() * 0.45) * k);
        ctx.fillStyle = `rgba(0, 0, 0, ${alpha.toFixed(4)})`;
        for (const dx of [-SIZE, 0, SIZE]) for (const dy of [-SIZE, 0, SIZE]) {
            const X = x + dx, Y = y + dy;
            if (X + r < 0 || Y + r < 0 || X - r > SIZE || Y - r > SIZE) continue;
            ctx.beginPath(); ctx.arc(X, Y, r, 0, Math.PI * 2); ctx.fill();
        }
    }
    return canvas;
}

// MINERAL: ROCK PLATES. Twenty-six plates — the cells of a Voronoi tiling of the torus (each seed's nine wrapped
// copies clip it), so the canvas tiles — each filled in its own grey (0.06–0.36 of black, scaled by MINERAL_K), with
// dark joints (0.6 of black) stroked along every edge, 10 texels wide (2.5 on the 256-texel preview the owner picked).
const PLATES = 26, JOINT = 10;
function plateCells(R) {
    const seeds = []; for (let i = 0; i < PLATES; i++) seeds.push([R() * SIZE, R() * SIZE, R()]);
    const copies = []; for (const [x, y] of seeds) for (const dx of [-SIZE, 0, SIZE]) for (const dy of [-SIZE, 0, SIZE]) copies.push([x + dx, y + dy]);
    return seeds.map(([sx, sy, shade]) => {
        let poly = [[sx - SIZE, sy - SIZE], [sx + SIZE, sy - SIZE], [sx + SIZE, sy + SIZE], [sx - SIZE, sy + SIZE]];
        for (const [ox, oy] of copies) {
            if (Math.abs(ox - sx) < 1e-9 && Math.abs(oy - sy) < 1e-9) continue;
            // keep the half-plane nearer (sx,sy) than (ox,oy): n·p <= c
            const nx = ox - sx, ny = oy - sy, c = (ox * ox + oy * oy - sx * sx - sy * sy) / 2, out = [];
            for (let i = 0; i < poly.length; i++) {
                const p = poly[i], q = poly[(i + 1) % poly.length], fp = nx * p[0] + ny * p[1] - c, fq = nx * q[0] + ny * q[1] - c;
                if (fp <= 0) out.push(p);
                if ((fp < 0) !== (fq < 0) && fp !== fq) { const t = fp / (fp - fq); out.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]); }
            }
            poly = out; if (!poly.length) break;
        }
        return { poly, shade };
    });
}
export function drawMineralCrust() {
    const scale = arguments[0], k = Number.isFinite(scale) ? scale : MINERAL_K;
    const { canvas, ctx } = blank(), cells = plateCells(rng(0x9e3779));
    const grey = (v) => { const g = Math.round(255 * (1 - Math.min(1, v))); return `rgb(${g}, ${g}, ${g})`; };
    const trace = (poly, dx, dy) => { ctx.beginPath(); poly.forEach(([x, y], i) => (i ? ctx.lineTo(x + dx, y + dy) : ctx.moveTo(x + dx, y + dy))); ctx.closePath(); };
    for (const pass of ['fill', 'joint']) for (const { poly, shade } of cells) for (const dx of [-SIZE, 0, SIZE]) for (const dy of [-SIZE, 0, SIZE]) {
        trace(poly, dx, dy);
        if (pass === 'fill') { ctx.fillStyle = grey((0.06 + shade * 0.3) * k); ctx.fill(); }
        else { ctx.strokeStyle = grey(0.6 * k); ctx.lineWidth = JOINT; ctx.lineJoin = 'round'; ctx.stroke(); }
    }
    return canvas;
}
